import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  addDays,
  format,
  parseISO,
  setHours,
  setMinutes
} from 'date-fns';
import { DateTimeRangeFilter } from '../local-types/DateTimeRangeFilter';

@Component({
  selector: 'app-datetime-range-filter',
  templateUrl: './dateTime-range-filter.component.html',
  styleUrls: ['./dateTime-range-filter.component.scss'],
})
export class DateTimeRangeFilterComponent implements OnInit, AfterViewInit { 

  @Input() public activateAfterViewInit!: boolean;
  @Output() public selectionChanged = new EventEmitter<DateTimeRangeFilter>();
  public filterActive!: boolean;

  public form: UntypedFormGroup = this.fb.group({
    fromDate: [new Date(), Validators.required],
    toDate: [addDays(new Date(), 1), Validators.required],
    fromTime: ['00:00', Validators.required],
    toTime: ['00:00', Validators.required],
  });

  private toDate!: Date;
  private fromDate!: Date;

  public constructor(private fb: UntypedFormBuilder) {}

  public ngOnInit(): void {
    this.filterActive = this.activateAfterViewInit;
  }

  public ngAfterViewInit(): void {
    if (this.activateAfterViewInit) {
      this.applyFilter();
    }
  }

  public setDatesFromString(from: string, to: string): void {
    this.toDate = parseISO(to);
    this.fromDate = parseISO(from);
    this.setDatesFromDate(this.fromDate, this.toDate);
  }

  public hasFilterSelected(): boolean {
    return this.filterActive;
  }

  public clearFilter(): void {
    if (this.toDate && this.fromDate) {
      this.setDatesFromDate(this.fromDate, this.toDate);
    } else {
      this.form.patchValue({
        fromDate: new Date(),
        toDate: addDays(new Date(), 1),
        fromTime: '00:00',
        toTime: '00:00',
      });
    }
    this.invokeSelectionChanged();
    this.filterActive = false;
  }

  public canApplyFilter(): boolean {
    return this.form.valid;
  }

  public applyFilter(): void {
    this.invokeSelectionChanged();
    this.filterActive = true;
  }

  public invokeSelectionChanged(): void {
    const formValue = this.form.value as {
      fromDate: Date;
      fromTime: string;
      toDate: Date;
      toTime: string;
    };

    // Time format: 00:00
    const fromHours = formValue.fromTime?.substring(0, 2);
    const fromMinutes = formValue.fromTime?.substring(3);
    const toHours = formValue.toTime?.substring(0, 2);
    const toMinutes = formValue.toTime?.substring(3);

    if (fromHours) {
      formValue.fromDate = setHours(formValue.fromDate, +fromHours);
    }
    if (fromMinutes) {
      formValue.fromDate = setMinutes(formValue.fromDate, +fromMinutes);
    }
    if (toHours) {
      formValue.toDate = setHours(formValue.toDate, +toHours);
    }
    if (toMinutes) {
      formValue.toDate = setMinutes(formValue.toDate, +toMinutes);
    }
    this.selectionChanged.emit({
      from: formValue.fromDate,
      to: formValue.toDate,
    } as DateTimeRangeFilter);
  }

  private setDatesFromDate(from: Date, to: Date): void {
    this.form.patchValue({
      fromDate: from,
      toDate: to,
      fromTime: format(from, 'HH:mm'),
      toTime: format(to, 'HH:mm'),
    });
    this.invokeSelectionChanged();
  }
}
